<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';

import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  currentPageInfo: {
    type: String,
    default: '',
  },
  showPageSizeSelector: {
    type: Boolean,
    default: false,
  },
  pageSizeOptions: {
    type: Array,
    default: () => [5, 10, 15, 30, 50, 100],
  },
});
const emit = defineEmits(['update:currentPage', 'update:itemsPerPage']);
const { t } = useI18n();
const { formatCompactNumber, formatFullNumber } = useNumberFormatter();

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.totalItems / props.itemsPerPage))
);
const startItem = computed(() =>
  props.totalItems === 0 ? 0 : (props.currentPage - 1) * props.itemsPerPage + 1
);
const endItem = computed(() =>
  Math.min(startItem.value + props.itemsPerPage - 1, props.totalItems)
);
const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage >= totalPages.value);
const pageSizeSelectOptions = computed(() =>
  props.pageSizeOptions.map(size => ({
    value: size,
    label: t('PAGINATION_FOOTER.PER_PAGE_TEMPLATE', { size }),
  }))
);
const selectedPageSize = computed({
  get: () => props.itemsPerPage,
  set: value => {
    emit('update:itemsPerPage', Number(value));
    if (props.currentPage !== 1) emit('update:currentPage', 1);
  },
});
const changePage = newPage => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    emit('update:currentPage', newPage);
  }
};

const currentPageInformation = computed(() => {
  const translationKey = props.currentPageInfo || 'PAGINATION_FOOTER.SHOWING';
  return t(
    translationKey,
    {
      startItem: formatFullNumber(startItem.value),
      endItem: formatFullNumber(endItem.value),
      totalItems: formatCompactNumber(props.totalItems),
    },
    Number(props.totalItems)
  );
});

const pageInfo = computed(() => {
  return t(
    'PAGINATION_FOOTER.CURRENT_PAGE_INFO',
    {
      currentPage: '',
      totalPages: formatCompactNumber(totalPages.value),
    },
    Number(totalPages.value)
  );
});
</script>

<template>
  <div
    class="relative flex min-h-[3.375rem] w-full flex-wrap items-center justify-between gap-2 border-t border-n-weak bg-n-surface-1 px-4 py-3 before:pointer-events-none before:absolute before:inset-x-0 before:-top-4 before:h-4 before:bg-gradient-to-t before:from-n-surface-1 before:from-0% before:to-transparent sm:px-6"
  >
    <div class="flex items-center gap-3">
      <span class="min-w-0 text-body-main line-clamp-1 text-n-slate-11">
        {{ currentPageInformation }}
      </span>
    </div>
    <div class="flex min-w-0 flex-wrap items-center gap-2">
      <FilterSelect
        v-if="showPageSizeSelector"
        v-model="selectedPageSize"
        :options="pageSizeSelectOptions"
        hide-icon
        variant="ghost"
      />
      <Button
        v-tooltip.top="t('PAGINATION_FOOTER.FIRST_PAGE')"
        icon="i-lucide-chevrons-left"
        variant="ghost"
        size="sm"
        color="slate"
        class="!w-8 !h-6"
        :aria-label="t('PAGINATION_FOOTER.FIRST_PAGE')"
        :disabled="isFirstPage"
        @click="changePage(1)"
      />
      <Button
        v-tooltip.top="t('PAGINATION_FOOTER.PREVIOUS_PAGE')"
        icon="i-lucide-chevron-left"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :aria-label="t('PAGINATION_FOOTER.PREVIOUS_PAGE')"
        :disabled="isFirstPage"
        @click="changePage(currentPage - 1)"
      />
      <div class="inline-flex items-center gap-2 text-sm">
        <span
          class="px-3 tabular-nums py-0.5 font-420 bg-n-input-background text-body-main text-n-slate-12 rounded-md"
        >
          {{ formatFullNumber(currentPage) }}
        </span>
        <span class="truncate text-body-main text-n-slate-11">
          {{ pageInfo }}
        </span>
      </div>
      <Button
        v-tooltip.top="t('PAGINATION_FOOTER.NEXT_PAGE')"
        icon="i-lucide-chevron-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :aria-label="t('PAGINATION_FOOTER.NEXT_PAGE')"
        :disabled="isLastPage"
        @click="changePage(currentPage + 1)"
      />
      <Button
        v-tooltip.top="t('PAGINATION_FOOTER.LAST_PAGE')"
        icon="i-lucide-chevrons-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :aria-label="t('PAGINATION_FOOTER.LAST_PAGE')"
        :disabled="isLastPage"
        @click="changePage(totalPages)"
      />
    </div>
  </div>
</template>
